/**
 * Lookbook Gallery Component
 * - Moves inline logic from the snippet into a reusable custom element.
 * - Ensures index-based image opening selects the correct slide reliably.
 * - Adds a 1s loader to the View More/Less toggle.
 */

/**
 * @param {number} ms
 * @returns {Promise<void>}
 */
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Promise that resolves on next animation frame.
 * @returns {Promise<void>}
 */
const nextAnimationFrame = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });

/**
 * Wait for the slideshow custom element to be defined and for the next animation frame.
 * @returns {Promise<void>}
 */
const whenSlideshowReady = async () => {
  await customElements.whenDefined('slideshow-component');
  await nextAnimationFrame();
};

class LookbookGalleryComponent extends HTMLElement {
  /** @type {HTMLElement | null} */ #grid = null;
  /** @type {HTMLElement | null} */ #dialogComponent = null;
  /** @type {HTMLElement | null} */ #slideshow = null;

  connectedCallback() {
    this.#grid = this.querySelector('[ref="grid"]');
    this.#dialogComponent = this.querySelector('#lookbookDialog');
    this.#slideshow = this.querySelector('#lookbook-slideshow');

    if (!this.#grid || !this.#dialogComponent || !this.#slideshow) return;

    this.#bindTiles();
    this.#bindClose();
    this.#bindViewMore();
  }

  #bindTiles() {
    this.#grid?.querySelectorAll('[data-index]')?.forEach((btn) => {
      btn.addEventListener('click', async () => {
        const index = Number(btn.getAttribute('data-index')) || 0;
        await this.#openDialogAndSelect(index);
      });
    });
  }

  /**
   * Open dialog then select a slideshow index.
   * @param {number} index
   */
  async #openDialogAndSelect(index) {
    const dialogComponent = this.#dialogComponent;
    const slideshow = this.#slideshow;
    if (!dialogComponent || !slideshow) return;

    // Open dialog first to ensure slideshow is connected/laid out
    /** @type {any} */
    (dialogComponent).showDialog?.();
    if (!(/** @type {any} */ (dialogComponent)).showDialog) {
      const dlg = dialogComponent.querySelector('dialog');
      if (dlg && typeof (dlg).showModal === 'function') (dlg).showModal();
    } else {
      // no-op; handled above
    }

    await whenSlideshowReady();

    // Prefer calling the component API directly for reliability
    try {
      // @ts-ignore runtime custom element instance
      if (typeof (slideshow).select === 'function') {
        // @ts-ignore runtime method from Slideshow element
        (slideshow).select(index, undefined, { animate: false });
      } else {
        // Fallback to attribute for older builds
        slideshow.setAttribute('initial-slide', String(index));
      }
    } catch {
      // Silent fallback to attribute
      slideshow.setAttribute('initial-slide', String(index));
    }
  }

  #bindClose() {
    // const closeBtn = this.querySelector('.close-button');
    // if (!closeBtn) return;
    // closeBtn.addEventListener('click', () => {
    //   const dialogComponent = this.#dialogComponent;
    //   if (!dialogComponent) return;
    //   /** @type {any} */
    //   (dialogComponent).closeDialog?.();
    //   if (!(/** @type {any} */ (dialogComponent)).closeDialog) {
    //     const dlg = dialogComponent.querySelector('dialog');
    //     if (dlg && typeof (dlg).close === 'function') (dlg).close();
    //   }
    // });
  }

  #bindViewMore() {
    const grid = this.#grid;
    const moreBtn = this.querySelector('[data-action="view-more"]');
    if (!grid || !moreBtn) return;

    const perPageAttr = grid.getAttribute('data-per-page');
    const step = perPageAttr ? parseInt(perPageAttr, 10) || 12 : 12;
    /** @type {HTMLElement[]} */
    let extras = Array.from(grid.querySelectorAll('.is-extra'));
    let revealedCount = 0;
    let allVisible = false;

    const updateLabels = () => {
      const labelMore = moreBtn.querySelector('[data-label-more]');
      const labelLess = moreBtn.querySelector('[data-label-less]');
      if (labelMore instanceof HTMLElement) labelMore.hidden = allVisible;
      if (labelLess instanceof HTMLElement) labelLess.hidden = !allVisible;
    };

    updateLabels();

    moreBtn.addEventListener('click', async () => {
      // Use theme loader pattern: aria-busy + spinner element
      const spinner = moreBtn.querySelector('.lookbook-view-more__spinner');
      const labelMore = moreBtn.querySelector('[data-label-more]');
      const labelLess = moreBtn.querySelector('[data-label-less]');
      if (labelMore instanceof HTMLElement) labelMore.classList.add('hidden');
      if (labelLess instanceof HTMLElement) labelLess.classList.add('hidden');
      if (spinner instanceof HTMLElement) spinner.classList.remove('hidden');
      moreBtn.setAttribute('aria-busy', 'true');
      moreBtn.setAttribute('disabled', 'true');

      await delay(1000);

      // Recompute in case DOM changed
      extras = Array.from(grid.querySelectorAll('.is-extra'));

      if (!allVisible) {
        const remaining = extras.slice(revealedCount);
        const toShow = remaining.slice(0, step);
        for (const el of toShow) {
          if (el instanceof HTMLElement) {
            el.style.display = 'block';
            el.setAttribute('data-visible', 'true');
          }
        }
        revealedCount += toShow.length;
        allVisible = revealedCount >= extras.length;
      } else {
        // Collapse back to initial 12
        for (const el of extras) {
          if (el instanceof HTMLElement) {
            el.style.removeProperty('display');
            el.removeAttribute('data-visible');
          }
        }
        revealedCount = 0;
        allVisible = false;

        // After collapsing, position the gallery so its bottom sits 100px above the viewport bottom
        await nextAnimationFrame();
        const container = this.querySelector('.lookbook-gallery') || this;
        const rect = container.getBoundingClientRect();
        const targetY = Math.max(0, window.scrollY + rect.bottom - (window.innerHeight - 100));
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      }

      // Reveal labels again and set correct visibility
      if (labelMore instanceof HTMLElement) labelMore.classList.remove('hidden');
      if (labelLess instanceof HTMLElement) labelLess.classList.remove('hidden');
      updateLabels();

      // Reset loader state
      if (spinner instanceof HTMLElement) spinner.classList.add('hidden');
      moreBtn.removeAttribute('aria-busy');
      moreBtn.removeAttribute('disabled');
    });
  }
}

if (!customElements.get('lookbook-gallery-component')) {
  customElements.define('lookbook-gallery-component', LookbookGalleryComponent);
}


